import React, { useMemo, useState } from "react";
import { Card, Button, Select, Table, Tag, Tooltip, Empty } from "antd";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import usePutAdapter from "../../../../customHooks/PUT_requests/usePutAdapter";
import { RootState } from "../../../../app/store";
import { AdapterInterface } from "../../../../interfaces/Adapter.interface";
import type { HallCallBinding, HallCallSlot } from "../../../../interfaces/IconDefinitions";
import {
  LN2_MESSAGE_CATEGORIES,
  type Ln2MessageCategory,
} from "../../../../flatbuffers/types/ln2MessageOptions";

/**
 * Authors the adapter's hall-call bindings.
 *
 * A binding tells the monitoring view "this LN2 message is a hall call, on this
 * indicator". It exists because only the `Traffic::*HallCall` family feeds
 * `state.hallCalls`, so a controller reporting its hall calls any other way
 * never lights a dot however the icon is configured — on a Swift CEC the up
 * hall call arrives as `Control::EnergizeUpHallCall`, carrying its floor, and
 * is discarded.
 *
 * Held per adapter because which message means "hall call" is a property of the
 * controller, not of the site or the org.
 *
 * The pickers are built from LN2_MESSAGE_CATEGORIES, which is derived from the
 * generated enums, so the category list and every value in it follow the schema
 * rather than a hand-kept copy. The encoding (BitMask vs OneHot) is taken from
 * the chosen category rather than asked for: it is a property of the message,
 * and asking an operator to get it right is asking them to know something the
 * schema already states.
 */

const SLOT_OPTIONS: Array<{ value: HallCallSlot; label: string }> = [
  { value: "upFront", label: "Up — front" },
  { value: "upRear", label: "Up — rear" },
  { value: "downFront", label: "Down — front" },
  { value: "downRear", label: "Down — rear" },
];

/** The socket_message_priority blob, of which bindings are one field. */
type SocketMessagePriority = {
  hallCallBindings?: HallCallBinding[];
  [key: string]: unknown;
};

function parsePriority(adapter?: AdapterInterface): SocketMessagePriority {
  try {
    const raw = adapter?.socketMessagePriorityJson;
    if (!raw) return {};
    return typeof raw === "string" ? (JSON.parse(raw) as SocketMessagePriority) : {};
  } catch {
    return {};
  }
}

export default function IconDefinitionHallCallBindings({
  adapter = undefined,
  isLoading = false,
}: {
  adapter?: AdapterInterface;
  isLoading?: boolean;
}) {
  const { theme } = useSelector((state: RootState) => state.home);
  const { putAdapter, isLoadingPutAdapter } = usePutAdapter();

  const priority = useMemo(() => parsePriority(adapter), [adapter]);
  const bindings: HallCallBinding[] = useMemo(
    () => (Array.isArray(priority.hallCallBindings) ? priority.hallCallBindings : []),
    [priority],
  );

  const [categoryKey, setCategoryKey] = useState<string>("control");
  const [messageValue, setMessageValue] = useState<number | undefined>(undefined);
  const [slot, setSlot] = useState<HallCallSlot>("upFront");

  const category: Ln2MessageCategory | undefined = useMemo(
    () => LN2_MESSAGE_CATEGORIES.find((c) => c.categoryKey === categoryKey),
    [categoryKey],
  );

  /**
   * Persist a new binding list.
   *
   * Spreads the existing blob rather than rebuilding it: displayMapMode,
   * iconStalenessTimeout and customConfiguration live alongside these, and
   * writing a fresh object would drop whichever fields this screen does not
   * happen to know about.
   */
  const persist = (next: HallCallBinding[]) => {
    if (!adapter) return;
    putAdapter({
      ...adapter,
      socketMessagePriorityJson: JSON.stringify({
        ...priority,
        hallCallBindings: next,
      }),
    });
  };

  const onAdd = () => {
    if (!category || messageValue === undefined) return;
    const candidate: HallCallBinding = {
      contentType: category.encodedMessageContent,
      messageType: category.messageContent,
      message: messageValue,
      slot,
    };
    // Identity plus slot is what makes a binding distinct. The same message
    // driving two indicators is legitimate; the same message driving the same
    // indicator twice is just a duplicate row.
    const exists = bindings.some(
      (b) =>
        b.contentType === candidate.contentType &&
        b.messageType === candidate.messageType &&
        b.message === candidate.message &&
        b.slot === candidate.slot,
    );
    if (exists) return;
    persist([...bindings, candidate]);
    setMessageValue(undefined);
  };

  const onDelete = (row: HallCallBinding) => {
    persist(
      bindings.filter(
        (b) =>
          !(
            b.contentType === row.contentType &&
            b.messageType === row.messageType &&
            b.message === row.message &&
            b.slot === row.slot
          ),
      ),
    );
  };

  /** Resolve a stored binding back to human labels for the table. */
  const describe = (b: HallCallBinding): { category: string; message: string } => {
    const cat = LN2_MESSAGE_CATEGORIES.find((c) => c.messageContent === b.messageType);
    const opt = cat?.options.find((o) => o.enumValue === b.message);
    return {
      category: cat?.label ?? `#${b.messageType}`,
      // A value with no matching option is shown as its number rather than
      // hidden — a binding written against a schema this build predates is
      // still real, and silently blanking it would hide a live configuration.
      message: opt?.label ?? `#${b.message}`,
    };
  };

  const columns = [
    {
      title: "Category",
      key: "category",
      render: (_: unknown, row: HallCallBinding) => <Tag>{describe(row).category}</Tag>,
    },
    {
      title: "Message",
      key: "message",
      render: (_: unknown, row: HallCallBinding) => describe(row).message,
    },
    {
      title: "Indicator",
      key: "slot",
      render: (_: unknown, row: HallCallBinding) =>
        SLOT_OPTIONS.find((s) => s.value === row.slot)?.label ?? row.slot,
    },
    {
      title: "",
      key: "actions",
      width: 60,
      render: (_: unknown, row: HallCallBinding) => (
        <Tooltip title="Remove this binding">
          <Button
            size="small"
            danger
            data-testid={`hall-call-binding-delete-${row.messageType}-${row.message}-${row.slot}`}
            disabled={isLoadingPutAdapter}
            onClick={() => onDelete(row)}
            icon={<FontAwesomeIcon icon={faTrash} />}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <Card
      title="Hall Call Bindings"
      className={`customCard__${theme} mt-3`}
      loading={isLoading}
      data-testid="hall-call-bindings-card"
    >
      <p className="generalStyles__info">
        Treat an LN2 message as a hall call. Use this when the controller reports hall calls with a
        message the monitoring view does not already recognise as one — a Swift CEC, for example,
        reports its up hall call as <code>Control: EnergizeUpHallCall</code>. The floor comes from
        the message itself, so one binding covers every floor, and the indicator follows the
        message&apos;s own active flag.
      </p>

      <div className="d-flex align-items-end" style={{ gap: 8, flexWrap: "wrap" }}>
        <div>
          <span className="generalStyles__inputLabel">Category</span>
          <Select
            data-testid="hall-call-binding-category"
            size="small"
            style={{ width: 180, display: "block" }}
            value={categoryKey}
            onChange={(v: string) => {
              setCategoryKey(v);
              setMessageValue(undefined);
            }}
            options={LN2_MESSAGE_CATEGORIES.map((c) => ({
              value: c.categoryKey,
              label: c.label,
            }))}
          />
        </div>
        <div>
          <span className="generalStyles__inputLabel">Message</span>
          <Select
            data-testid="hall-call-binding-message"
            size="small"
            showSearch
            optionFilterProp="label"
            placeholder="Select a message"
            style={{ width: 260, display: "block" }}
            value={messageValue}
            onChange={(v: number) => setMessageValue(v)}
            options={(category?.options ?? []).map((o) => ({
              value: o.enumValue,
              label: o.label,
            }))}
          />
        </div>
        <div>
          <span className="generalStyles__inputLabel">Indicator</span>
          <Select
            data-testid="hall-call-binding-slot"
            size="small"
            style={{ width: 160, display: "block" }}
            value={slot}
            onChange={(v: HallCallSlot) => setSlot(v)}
            options={SLOT_OPTIONS}
          />
        </div>
        <Button
          type="primary"
          size="small"
          data-testid="hall-call-binding-add"
          disabled={messageValue === undefined || isLoadingPutAdapter}
          onClick={onAdd}
          icon={<FontAwesomeIcon icon={faPlus} />}
        >
          Add
        </Button>
      </div>

      <Table
        className="mt-3"
        size="small"
        rowKey={(row: HallCallBinding) => `${row.messageType}:${row.message}:${row.slot}`}
        dataSource={bindings}
        columns={columns}
        pagination={false}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No bindings. The monitoring view uses only its built-in hall-call messages."
            />
          ),
        }}
      />
    </Card>
  );
}
